"use client"

import { useEffect, useMemo, useState } from "react"
import axios from "axios"
import {
  FaCheckCircle,
  FaTimesCircle,
  FaTrash,
  FaMoneyBillWave,
  FaFilter,
  FaExchangeAlt,
  FaHourglassHalf,
  FaFilePdf,
  FaPlus,
} from "react-icons/fa"
import "../styles/FinanceiroAdmin.css"
import PainelParcelamentos from "../components/admin/PainelParcelamentos"

const API = "https://api.ticketdovaqueiro.com/api"

const FinanceiroAdmin = () => {
  const [saques, setSaques] = useState([])
  const [entradas, setEntradas] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingEntradas, setLoadingEntradas] = useState(false)
  const [salvandoEntrada, setSalvandoEntrada] = useState(false)
  const [mensagem, setMensagem] = useState("")
  const [tipoMensagem, setTipoMensagem] = useState("")
  const [resumo, setResumo] = useState({
    totalDisponivel: 0,
    totalSaquesAprovados: 0,
    totalSaquesPendentes: 0,
    totalEntradas: 0,
    totalVendasLiquidas: 0,
  })
  const [donos, setDonos] = useState([])
  const [eventos, setEventos] = useState([])
  const [donoSelecionado, setDonoSelecionado] = useState("")
  const [eventoSelecionado, setEventoSelecionado] = useState("")
  const [formEntrada, setFormEntrada] = useState({
    tipo: "patrocinio",
    valor: "",
    descricao: "",
    dataEntrada: new Date().toISOString().slice(0, 10),
  })

  const token = () => localStorage.getItem("token")

  useEffect(() => {
    fetchDonos()
    fetchEventos()
  }, [])

  useEffect(() => {
    fetchSaques()
    fetchResumo()
    fetchEntradas()
  }, [donoSelecionado, eventoSelecionado])

  const eventosFiltrados = useMemo(() => {
    if (!donoSelecionado) return eventos
    return eventos.filter((evento) => {
      const donoId = evento.dono?._id || evento.dono
      return String(donoId || "") === String(donoSelecionado)
    })
  }, [eventos, donoSelecionado])

  useEffect(() => {
    if (!eventoSelecionado) return
    const aindaExiste = eventosFiltrados.some((evento) => String(evento._id) === String(eventoSelecionado))
    if (!aindaExiste) setEventoSelecionado("")
  }, [eventosFiltrados, eventoSelecionado])

  const fetchDonos = async () => {
    try {
      const res = await axios.get(`${API}/admin/donos`, {
        headers: { Authorization: `Bearer ${token()}` },
      })
      setDonos(res.data)
    } catch (err) {
      console.error("Erro ao carregar donos:", err)
    }
  }

  const fetchEventos = async () => {
    try {
      const res = await axios.get(`${API}/admin/eventos`, {
        headers: { Authorization: `Bearer ${token()}` },
      })
      setEventos(res.data)
    } catch (err) {
      console.error("Erro ao carregar eventos:", err)
    }
  }

  const fetchSaques = async () => {
    try {
      setLoading(true)
      const params = { donoId: donoSelecionado || undefined }
      const res = await axios.get(`${API}/admin/financeiro/saques`, {
        headers: { Authorization: `Bearer ${token()}` },
        params,
      })
      setSaques(res.data)
    } catch (err) {
      console.error("Erro ao carregar saques:", err)
    } finally {
      setLoading(false)
    }
  }

  const fetchResumo = async () => {
    try {
      const params = { donoId: donoSelecionado || undefined }
      const res = await axios.get(`${API}/admin/financeiro/admin-resumo`, {
        headers: { Authorization: `Bearer ${token()}` },
        params,
      })
      setResumo(res.data)
    } catch (err) {
      console.error("Erro ao carregar resumo financeiro:", err)
    }
  }

  const fetchEntradas = async () => {
    try {
      setLoadingEntradas(true)
      const params = {
        donoId: donoSelecionado || undefined,
        eventoId: eventoSelecionado || undefined,
      }
      const res = await axios.get(`${API}/admin/financeiro/entradas`, {
        headers: { Authorization: `Bearer ${token()}` },
        params,
      })
      setEntradas(res.data?.entradas || [])
    } catch (err) {
      console.error("Erro ao carregar entradas:", err)
    } finally {
      setLoadingEntradas(false)
    }
  }

  const adicionarEntrada = async (e) => {
    e.preventDefault()

    if (!eventoSelecionado) {
      setMensagem("Selecione a vaquejada/evento antes de adicionar a entrada.")
      setTipoMensagem("error")
      return
    }

    const valor = Number(String(formEntrada.valor).replace(",", "."))
    if (!Number.isFinite(valor) || valor <= 0) {
      setMensagem("Informe um valor maior que zero.")
      setTipoMensagem("error")
      return
    }

    if (!formEntrada.descricao.trim()) {
      setMensagem("Informe uma descrição para a entrada.")
      setTipoMensagem("error")
      return
    }

    try {
      setSalvandoEntrada(true)
      await axios.post(
        `${API}/admin/financeiro/entradas`,
        {
          eventoId: eventoSelecionado,
          tipo: formEntrada.tipo,
          valor,
          descricao: formEntrada.descricao.trim(),
          dataEntrada: formEntrada.dataEntrada || undefined,
        },
        { headers: { Authorization: `Bearer ${token()}` } },
      )

      setFormEntrada({
        tipo: "patrocinio",
        valor: "",
        descricao: "",
        dataEntrada: new Date().toISOString().slice(0, 10),
      })
      setMensagem("Entrada adicionada ao financeiro do evento com sucesso!")
      setTipoMensagem("success")
      await Promise.all([fetchEntradas(), fetchResumo()])
    } catch (err) {
      console.error("Erro ao adicionar entrada:", err)
      setMensagem(err.response?.data?.mensagem || "Erro ao adicionar entrada.")
      setTipoMensagem("error")
    } finally {
      setSalvandoEntrada(false)
    }
  }

  const cancelarEntrada = async (id) => {
    const motivo = window.prompt("Motivo do cancelamento (opcional):")
    if (motivo === null) return
    if (!window.confirm("Cancelar esta entrada? Ela deixará de compor a arrecadação e o saldo.")) return

    try {
      await axios.patch(
        `${API}/admin/financeiro/entradas/${id}/cancelar`,
        { motivo: motivo.trim() },
        { headers: { Authorization: `Bearer ${token()}` } },
      )
      setMensagem("Entrada cancelada. O valor foi retirado da arrecadação.")
      setTipoMensagem("success")
      await Promise.all([fetchEntradas(), fetchResumo()])
    } catch (err) {
      console.error("Erro ao cancelar entrada:", err)
      setMensagem(err.response?.data?.mensagem || "Erro ao cancelar entrada.")
      setTipoMensagem("error")
    }
  }

  const aprovarSaque = async (id) => {
    if (!window.confirm("Confirmar a transferência e aprovar este saque?")) return

    const descricaoAprovacao = window.prompt(
      "Informe a descrição deste saque aprovado. Ex.: Repasse final do evento X, adiantamento, acerto parcial...",
    )
    if (descricaoAprovacao === null) return

    const descricaoLimpa = descricaoAprovacao.trim()
    if (!descricaoLimpa) {
      setMensagem("Informe uma descrição para aprovar o saque.")
      setTipoMensagem("error")
      return
    }
    if (descricaoLimpa.length > 500) {
      setMensagem("A descrição do saque deve ter no máximo 500 caracteres.")
      setTipoMensagem("error")
      return
    }

    try {
      await axios.put(
        `${API}/admin/financeiro/aprovar-saque/${id}`,
        { descricaoAprovacao: descricaoLimpa },
        { headers: { Authorization: `Bearer ${token()}` } },
      )
      setMensagem("Saque aprovado com sucesso!")
      setTipoMensagem("success")
      await Promise.all([fetchSaques(), fetchResumo()])
    } catch (err) {
      console.error("Erro ao aprovar saque:", err.response?.data?.mensagem || err)
      setMensagem(err.response?.data?.mensagem || "Erro ao aprovar saque.")
      setTipoMensagem("error")
    }
  }

  const cancelarSaque = async (id) => {
    if (!window.confirm("Deseja realmente RECUSAR/CANCELAR este pedido de saque? O valor voltará para o dono.")) return
    try {
      await axios.delete(`${API}/admin/financeiro/cancelar-saque/${id}`, {
        headers: { Authorization: `Bearer ${token()}` },
      })
      setMensagem("Saque cancelado com sucesso!")
      setTipoMensagem("success")
      await Promise.all([fetchSaques(), fetchResumo()])
    } catch (err) {
      console.error("Erro ao cancelar saque:", err)
      setMensagem("Erro ao cancelar saque.")
      setTipoMensagem("error")
    }
  }

  const deletarSaque = async (id) => {
    if (!window.confirm("Deseja APAGAR este registro do histórico permanentemente? Essa ação não pode ser desfeita.")) return
    try {
      await axios.delete(`${API}/admin/financeiro/deletar-saque/${id}`, {
        headers: { Authorization: `Bearer ${token()}` },
      })
      setMensagem("Registro excluído do histórico com sucesso!")
      setTipoMensagem("success")
      fetchSaques()
    } catch (err) {
      console.error("Erro ao excluir saque:", err)
      setMensagem(err.response?.data?.mensagem || "Erro ao excluir registro.")
      setTipoMensagem("error")
    }
  }

  const baixarRelatorioSaquesPdf = async () => {
    try {
      const params = {}
      if (donoSelecionado) params.donoId = donoSelecionado

      const response = await axios.get(`${API}/admin/financeiro/relatorio-saques-pdf`, {
        headers: { Authorization: `Bearer ${token()}` },
        params,
        responseType: "blob",
      })

      const blob = new Blob([response.data], { type: "application/pdf" })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      const dono = donos.find((item) => item._id === donoSelecionado)
      const nomeDono = (dono?.nomeCompleto || "todos-os-donos")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase()

      link.href = url
      link.download = `relatorio-saques-${nomeDono || "saques"}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error("Erro ao baixar relatório de saques:", err)
      setMensagem(err.response?.data?.mensagem || "Erro ao baixar relatório de saques.")
      setTipoMensagem("error")
    }
  }

  const moeda = (valor) => Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  })

  return (
    <div className="financeiro-admin-container">
      <h2>
        <FaMoneyBillWave /> Gestão Financeira
      </h2>

      <div className="filtro-evento">
        <label>
          <FaFilter /> Filtrar por Dono:
        </label>
        <select value={donoSelecionado} onChange={(e) => setDonoSelecionado(e.target.value)}>
          <option value="">Todos os Donos</option>
          {donos.map((dono) => (
            <option key={dono._id} value={dono._id}>{dono.nomeCompleto}</option>
          ))}
        </select>

        <label>Vaquejada / Evento:</label>
        <select value={eventoSelecionado} onChange={(e) => setEventoSelecionado(e.target.value)}>
          <option value="">Todos os eventos</option>
          {eventosFiltrados.map((evento) => (
            <option key={evento._id} value={evento._id}>{evento.nome}</option>
          ))}
        </select>

        <button
          type="button"
          className="btn-baixar-relatorio-saques"
          onClick={baixarRelatorioSaquesPdf}
          title="Baixar relatório em PDF com os saques do dono selecionado"
        >
          <FaFilePdf /> Baixar PDF de saques
        </button>
      </div>

      <div className="grid-resumo-admin">
        <div className="grid-item-admin">
          <h4>Total Disponível</h4>
          <p>{moeda(resumo.totalDisponivel)}</p>
        </div>
        <div className="grid-item-admin">
          <h4>Vendas Líquidas</h4>
          <p>{moeda(resumo.totalVendasLiquidas)}</p>
        </div>
        <div className="grid-item-admin">
          <h4>Entradas Externas</h4>
          <p>{moeda(resumo.totalEntradas)}</p>
        </div>
        <div className="grid-item-admin">
          <h4>Saques Aprovados</h4>
          <p>{moeda(resumo.totalSaquesAprovados)}</p>
        </div>
        <div className="grid-item-admin">
          <h4>Saques Pendentes</h4>
          <p>{moeda(resumo.totalSaquesPendentes)}</p>
        </div>
      </div>

      {mensagem && <div className={`mensagem ${tipoMensagem}`}>{mensagem}</div>}

      <div className="tabela-financeira" style={{ marginBottom: 24 }}>
        <h2><FaPlus /> Entradas da Vaquejada</h2>
        <p style={{ opacity: 0.75 }}>
          Use para valores que não vieram de vendas, como aporte ou patrocínio. Essas entradas somam no total arrecadado e não sofrem taxa do site.
        </p>

        <form
          onSubmit={adicionarEntrada}
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(180px, 1fr) 150px 150px 160px minmax(220px, 2fr) auto",
            gap: 10,
            alignItems: "end",
            marginBottom: 20,
          }}
        >
          <div>
            <label>Evento</label>
            <select
              value={eventoSelecionado}
              onChange={(e) => setEventoSelecionado(e.target.value)}
              style={{ width: "100%" }}
              required
            >
              <option value="">Selecione...</option>
              {eventosFiltrados.map((evento) => (
                <option key={evento._id} value={evento._id}>{evento.nome}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Tipo</label>
            <select
              value={formEntrada.tipo}
              onChange={(e) => setFormEntrada((old) => ({ ...old, tipo: e.target.value }))}
              style={{ width: "100%" }}
            >
              <option value="patrocinio">Patrocínio</option>
              <option value="aporte">Aporte</option>
            </select>
          </div>
          <div>
            <label>Valor</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={formEntrada.valor}
              onChange={(e) => setFormEntrada((old) => ({ ...old, valor: e.target.value }))}
              style={{ width: "100%" }}
              required
            />
          </div>
          <div>
            <label>Data</label>
            <input
              type="date"
              value={formEntrada.dataEntrada}
              onChange={(e) => setFormEntrada((old) => ({ ...old, dataEntrada: e.target.value }))}
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label>Descrição</label>
            <input
              type="text"
              maxLength={500}
              placeholder="Ex.: Patrocínio Empresa X"
              value={formEntrada.descricao}
              onChange={(e) => setFormEntrada((old) => ({ ...old, descricao: e.target.value }))}
              style={{ width: "100%" }}
              required
            />
          </div>
          <button type="submit" disabled={salvandoEntrada}>
            {salvandoEntrada ? "Salvando..." : "Adicionar"}
          </button>
        </form>

        {loadingEntradas ? (
          <p>Carregando entradas...</p>
        ) : entradas.length === 0 ? (
          <div className="empty-state"><p>Nenhuma entrada encontrada.</p></div>
        ) : (
          <div className="table-container">
            <table className="extrato-tabela">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Evento</th>
                  <th>Dono</th>
                  <th>Tipo</th>
                  <th>Descrição</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {entradas.map((entrada) => (
                  <tr key={entrada._id} style={{ opacity: entrada.status === "cancelado" ? 0.55 : 1 }}>
                    <td>{new Date(entrada.dataEntrada || entrada.createdAt).toLocaleDateString("pt-BR")}</td>
                    <td>{entrada.evento?.nome || "N/A"}</td>
                    <td>{entrada.dono?.nomeCompleto || "N/A"}</td>
                    <td>{entrada.tipo === "patrocinio" ? "Patrocínio" : "Aporte"}</td>
                    <td>{entrada.descricao || "-"}</td>
                    <td style={{ fontWeight: 700, color: entrada.status === "ativo" ? "#10b981" : "#888" }}>
                      {moeda(entrada.valor)}
                    </td>
                    <td>{entrada.status === "ativo" ? "Ativa" : "Cancelada"}</td>
                    <td>
                      {entrada.status === "ativo" ? (
                        <FaTimesCircle
                          className="icone-acao vermelho"
                          onClick={() => cancelarEntrada(entrada._id)}
                          title="Cancelar entrada"
                          style={{ cursor: "pointer" }}
                        />
                      ) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="tabela-financeira">
        <h2><FaExchangeAlt /> Extratos de Saques</h2>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Carregando saques...</p>
          </div>
        ) : saques.length === 0 ? (
          <div className="empty-state"><p>Nenhum saque encontrado.</p></div>
        ) : (
          <div className="table-container">
            <table className="extrato-tabela">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Nome Completo</th>
                  <th>Whatsapp</th>
                  <th>Valor</th>
                  <th>Tipo Pix</th>
                  <th>Chave Pix</th>
                  <th>Descrição</th>
                  <th>Status</th>
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {saques.map((saque) => (
                  <tr key={saque._id} style={{ opacity: saque.status === "cancelado" ? 0.6 : 1 }}>
                    <td>
                      {saque.status === "pendente"
                        ? new Date(saque.dataSolicitacao).toLocaleString("pt-BR")
                        : saque.dataAtualizacao
                          ? new Date(saque.dataAtualizacao).toLocaleString("pt-BR")
                          : new Date(saque.dataSolicitacao).toLocaleString("pt-BR")}
                    </td>
                    <td>{saque.donoId?.nomeCompleto || "N/A"}</td>
                    <td>{saque.donoId?.whatsapp || "N/A"}</td>
                    <td
                      style={{
                        fontWeight: "bold",
                        color: saque.status === "cancelado" ? "#888" : "#10b981",
                        textDecoration: saque.status === "cancelado" ? "line-through" : "none",
                      }}
                    >
                      {moeda(saque.valor)}
                    </td>
                    <td>{saque.chavePix?.tipo || "-"}</td>
                    <td>{saque.chavePix?.valor || "-"}</td>
                    <td>{saque.descricaoAprovacao || "-"}</td>
                    <td>
                      <span className={`status ${saque.status}`}>
                        {saque.status === "aprovado" && "Transferido"}
                        {saque.status === "pendente" && <><FaHourglassHalf /> Pendente</>}
                        {saque.status === "cancelado" && "Cancelado"}
                      </span>
                    </td>
                    <td style={{ display: "flex", gap: "8px", alignItems: "center", justifyContent: "center" }}>
                      {saque.status === "pendente" && (
                        <>
                          <FaCheckCircle
                            className="icone-acao verde"
                            onClick={() => aprovarSaque(saque._id)}
                            title="Aprovar saque"
                          />
                          <FaTimesCircle
                            className="icone-acao vermelho"
                            onClick={() => cancelarSaque(saque._id)}
                            title="Cancelar saque"
                          />
                        </>
                      )}
                      {(saque.status === "cancelado" || saque.status === "aprovado") && (
                        <FaTrash
                          className="icone-acao vermelho"
                          style={{ color: "#dc2626", cursor: "pointer" }}
                          onClick={() => deletarSaque(saque._id)}
                          title="Apagar registro do histórico"
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PainelParcelamentos donoId={donoSelecionado} />
    </div>
  )
}

export default FinanceiroAdmin
